import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { HeartIcon } from "./HeartIcon.js";
import { useReward } from "partycles";

interface LikePillProps {
  liked: boolean;
  count: number;
  size?: "small" | "medium";
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

export const LikePill: React.FC<LikePillProps> = ({
  liked,
  count,
  onClick,
  disabled = false,
  loading = false,
  className = "",
  size = "medium",
}) => {
  const { t } = useTranslation();
  const btnRef = useRef<HTMLButtonElement>(null);

  const { reward, isAnimating } = useReward(btnRef, "hearts", {
    particleCount: 10,
    spread: 50,
    startVelocity: 10,
    colors: ["#ff1744", "#ff8a65", "#f06292"],
    elementSize: 25,
  });
  const handleClick = async (e: React.MouseEvent) => {
    if (disabled || loading || isAnimating) return;
    if (!liked) reward();

    onClick(e);
  };

  return (
    <button
      onClick={handleClick}
      ref={btnRef}
      disabled={disabled || loading || isAnimating}
      data-testid="like-pill"
      aria-label={liked ? t("likes.removeFromFavorites") : t("likes.addToFavorites")}
      className={`
        flex items-center gap-2  rounded-full
        ${size === "small" ? "text-xs px-2 py-1" : "px-3 py-2"}
        transition-all duration-200
        ${liked ? "bg-red-500/95 hover:bg-red-600 border border-red-500" : "border border-white bg-white/80 hover:bg-white"}
        ${disabled || loading || isAnimating ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        shadow-md hover:shadow-lg
        ${className}
      `}
      title={t("likes.peopleLiked", { count })}
    >
      {/* Count text */}
      <span
        className={`
          ${size === "small" ? "text-xs" : "text-sm"} font-semibold transition-colors duration-200
          ${liked ? "text-white" : "text-gray-800"}
        `}
      >
        {count}
      </span>

      {/* Heart icon without background circle */}
      <div className="flex items-center justify-center">
        <svg
          width={size === "small" ? 16 : 18}
          height={size === "small" ? 16 : 18}
          viewBox="0 0 24 24"
          className={`transition-colors duration-200 ${
            liked ? "text-white" : "text-red-500"
          }`}
        >
          {liked ? (
            // Filled heart
            <path
              fill="currentColor"
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            />
          ) : (
            // Heart outline
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            />
          )}
        </svg>
      </div>
    </button>
  );
};
